import { Skeleton } from "@/components/ui/skeleton";
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as UITooltip } from "@/components/ui/tooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { formatTooltipTime, formatXAxisTick, GroupByType } from "@/utils/dateUtils";
import { CALL_VOLUME_COLORS } from "@/constants/colors";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Info } from "lucide-react";
import { LEGEND_WRAPPER_STYLE } from "@/lib/chartConfig";

interface CallVolumeWidgetData {
  time: Date;
  inboundDirectCall: number;
  internalCall: number;
  outboundCall: number;
}

/**
 *
 * @param dateRangeType
 * @param dateRange
 *
 * - When dateRange is the same day, return "hour"
 * - When dateRange is the same month, return "day"
 * - When dateRange is the same year, return "month"
 * - When dateRange is not the same year, return "day"
 *
 * @returns
 *
 */
const getGroupByFromDateRange = (dateRangeType: string, dateRange?: { from: Date, to: Date }): GroupByType => {
  if (!dateRange) return "day";

  const isDateRangeTheSameYear = dateRange.from.getFullYear() === dateRange.to.getFullYear();
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, dateRange, theme, groupBy }: any) => {
  if (active && payload && payload.length) {
    const currentInboundDirectColor = theme === 'dark' ? CALL_VOLUME_COLORS.INBOUND_DIRECT.dark : CALL_VOLUME_COLORS.INBOUND_DIRECT.light;
    const currentInternalColor = theme === 'dark' ? CALL_VOLUME_COLORS.INTERNAL.dark : CALL_VOLUME_COLORS.INTERNAL.light;
    const currentOutboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light;

    return (
      <div className="bg-white dark:bg-gray-800 p-3 border dark:border-gray-600 rounded shadow-lg">
        <p className="font-semibold dark:text-white">{formatTooltipTime(label, groupBy)}</p>
        <p style={{ color: currentOutboundColor }}>Outbound: {payload[0].value}</p>
        <p style={{ color: currentInboundDirectColor }}>Inbound Direct: {payload[1].value}</p>
        <p style={{ color: currentInternalColor }}>Internal: {payload[2].value}</p>
      </div>
    );
  }
  return null;
};

const CallVolumeDistributionWidget: React.FC<{ data?: { callOverviewData?: { callVolumeDistribution?: CallVolumeWidgetData[] } }; dateRangeType: string, dateRange: { from: Date, to: Date }, icon?: React.ReactNode; loading?: boolean; error?: string }> = ({ data, dateRangeType, dateRange, loading }) => {
  const { theme } = useDashboard();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tickGap, setTickGap] = useState<number>(1);

  // Convert data to local timezone and group by month if needed
  const callVolumeData = data?.callOverviewData?.callVolumeDistribution || [];
  const processedData = callVolumeData?.map(item => ({
    ...item,
    time: new Date(item.time)
  }));

  // Update dateRange based on data if available
  if (processedData && processedData.length > 0) {
    const times = processedData.map(item => item.time.getTime());
    const minTime = new Date(Math.min(...times));
    const maxTime = new Date(Math.max(...times));
    dateRange = {
      from: minTime,
      to: maxTime
    };
  }

  const groupBy: GroupByType = getGroupByFromDateRange(dateRangeType, dateRange);

  // Check if all data points are 0
  const isAllDataZero = processedData?.every(item =>
    item.inboundDirectCall === 0 && item.internalCall === 0 && item.outboundCall === 0
  ) ?? false;

  // Calculate tick gap based on container width and data length
  useEffect(() => {
    if (containerRef.current && processedData?.length) {
      const items = processedData.length;
      const width = (containerRef.current.container?.clientWidth || containerRef.current.offsetWidth || 600) - 60 - 25;
      const required = groupBy === 'daydiffyear' ? 80 : 60;
      const calculated = Math.ceil(required / (width / items));
      setTickGap(calculated);
    }
  }, [groupBy, processedData]);

  // Get current colors based on theme
  const currentInboundDirectColor = theme === 'dark' ? CALL_VOLUME_COLORS.INBOUND_DIRECT.dark : CALL_VOLUME_COLORS.INBOUND_DIRECT.light;
  const currentInternalColor = theme === 'dark' ? CALL_VOLUME_COLORS.INTERNAL.dark : CALL_VOLUME_COLORS.INTERNAL.light;
  const currentOutboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light;

  return (
    <div className="w-full min-w-[200px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3">
      <div ref={containerRef} className="flex flex-row items-center gap-2">
        <div className="text-[14x] font-[500] text-primary dark:text-white truncate">Call Volume Distribution</div>
        <TooltipProvider>
          <UITooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <Info className="h-4 w-4 text-primary dark:text-white" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-xs text-xs">The total number of inbound direct calls, outbound calls and internal calls within the given time.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-56 w-full my-3" />
        ) : processedData?.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
            <div className="flex flex-col items-center gap-2">
              <span>No call records were found</span>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={processedData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 14, fill: 'currentColor' }}
                tickFormatter={(value) => formatXAxisTick(value, groupBy)}
                interval={0}
                stroke="currentColor"
                ticks={processedData && processedData.length > tickGap
                  ? Array.from({ length: Math.ceil(processedData.length / tickGap) }, (_, i) => processedData[i * tickGap]?.time).filter(Boolean)
                  : processedData?.map(d => d.time)}
              />
              <YAxis
                tick={{ fontSize: 14, fill: 'currentColor' }}
                stroke="currentColor"
                domain={isAllDataZero ? [0, 10] : undefined}
                ticks={isAllDataZero ? [0, 2, 4, 6, 8, 10] : undefined}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} dateRange={dateRangeType} theme={theme} groupBy={groupBy} />} cursor={{ fill: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)" }} />
              <Legend
                wrapperStyle={LEGEND_WRAPPER_STYLE}
                verticalAlign="top"
                align="center"
                iconType="rect"
                formatter={(value) => {
                  let color;
                  if (value === 'Inbound Direct') color = currentInboundDirectColor;
                  else if (value === 'Internal') color = currentInternalColor;
                  else if (value === 'Outbound') color = currentOutboundColor;
                  return <span style={{ color }}>{value}</span>;
                }}
              />
              <Bar dataKey="outboundCall" name="Outbound" fill={currentOutboundColor} radius={[4, 4, 0, 0]} />
              <Bar dataKey="inboundDirectCall" name="Inbound Direct" fill={currentInboundDirectColor} radius={[4, 4, 0, 0]} />
              <Bar dataKey="internalCall" name="Internal" fill={currentInternalColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CallVolumeDistributionWidget;
